import { useEffect, useState } from "react";
import { ApiError, api } from "./client";

type QueryValue = string | number | null | undefined;
type QueryParams = Record<string, QueryValue>;

export type ReportFilter = {
  branchId?: string;
  startDate?: string;
  endDate?: string;
};

export type PagedReportFilter = ReportFilter & {
  status?: "active" | "passive" | "graduated" | "";
  trainingPeriodId?: string;
  trainingGroupId?: string;
  trainingSubgroupId?: string;
  limit?: number;
  offset?: number;
};

export type DailyRangeFilter = {
  branchId?: string;
  startDate: string;
  endDate: string;
};

export type AppliedFilter = {
  branch_id: string | null;
  start_date: string | null;
  end_date: string | null;
  status?: string | null;
};

export type InstructorSummaryRow = {
  instructor_id: string;
  instructor_name: string;
  employment_type: string;
  is_active: boolean;
  branch_id: string;
  total_appointments: number;
  completed: number;
  cancelled: number;
  planned: number;
  total_lesson_minutes: number;
  unique_students: number;
  first_lesson_at: string | null;
  last_lesson_at: string | null;
  reported_attended: number;
  no_show_count: number;
  total_km: number;
  avg_driving_score: number | null;
};

export type VehicleSummaryRow = {
  vehicle_id: string;
  plate_no: string;
  brand_model: string | null;
  gear_type: string | null;
  is_active: boolean;
  branch_id: string;
  current_km: number;
  last_service_date: string | null;
  next_service_km: number | null;
  next_service_date: string | null;
  insurance_expiry_date: string | null;
  inspection_expiry_date: string | null;
  total_appointments: number;
  completed: number;
  cancelled: number;
  total_lesson_minutes: number;
  unique_instructors: number;
  unique_students: number;
  last_lesson_at: string | null;
  total_km: number;
};

export type StudentSummaryRow = {
  student_id: string;
  student_name: string;
  training_period_label: string | null;
  training_group_label: string | null;
  training_subgroup_label: string | null;
  identity_no: string | null;
  exam_date: string | null;
  student_status: string;
  branch_id: string;
  total_appointments: number;
  total_written: number;
  completed: number;
  cancelled: number;
  planned: number;
  total_lesson_minutes: number;
  unique_instructors: number;
  last_lesson_at: string | null;
  reported_attended: number;
  no_show_count: number;
  total_km: number;
  avg_driving_score: number | null;
  days_to_exam: number | null;
};

export type DailySummaryRow = {
  day: string;
  total_appointments: number;
  completed: number;
  cancelled: number;
  planned: number;
  total_lesson_minutes: number;
  active_instructors: number;
  active_vehicles: number;
  unique_students: number;
  total_km: number;
  no_show_count: number;
};

export type TopicsCoverageRow = {
  topic_key: string;
  report_count: number;
  percentage: number;
};

export type BranchOverviewRow = {
  branch_id: string;
  branch_name: string;
  active_students: number;
  passive_students: number;
  graduated_students: number;
  active_instructors: number;
  active_vehicles: number;
  total_appointments: number;
  completed: number;
  cancelled: number;
  total_lesson_minutes: number;
  total_km: number;
  no_show_count: number;
};

type Wrapped<T> = { items: T[]; filter: AppliedFilter };
type PagedWrapped<T> = { items: T[]; total: number; limit: number; offset: number; filter: AppliedFilter };
type TopicsResponse = { items: TopicsCoverageRow[]; total_attended_reports: number; filter: AppliedFilter };

function buildQuery(
  filter: ReportFilter & {
    status?: string;
    trainingPeriodId?: string;
    trainingGroupId?: string;
    trainingSubgroupId?: string;
    limit?: number;
    offset?: number;
  },
) {
  return {
    branch_id: filter.branchId || undefined,
    start_date: filter.startDate || undefined,
    end_date: filter.endDate || undefined,
    status: filter.status === "" ? undefined : filter.status ?? "active",
    training_period_id: filter.trainingPeriodId || undefined,
    training_group_id: filter.trainingGroupId || undefined,
    training_subgroup_id: filter.trainingSubgroupId || undefined,
    limit: filter.limit,
    offset: filter.offset,
  };
}

export function useInstructorSummary(filter: ReportFilter) {
  return useReportFetch<Wrapped<InstructorSummaryRow>>(
    "/reports/instructor-summary",
    buildQuery(filter),
    [filter.branchId, filter.startDate, filter.endDate],
  );
}

export function useVehicleSummary(filter: ReportFilter) {
  return useReportFetch<Wrapped<VehicleSummaryRow>>(
    "/reports/vehicle-summary",
    buildQuery(filter),
    [filter.branchId, filter.startDate, filter.endDate],
  );
}

export function useStudentSummary(filter: PagedReportFilter) {
  return useReportFetch<PagedWrapped<StudentSummaryRow>>(
    "/reports/student-summary",
    buildQuery(filter),
    [
      filter.branchId,
      filter.startDate,
      filter.endDate,
      filter.status,
      filter.trainingPeriodId,
      filter.trainingGroupId,
      filter.trainingSubgroupId,
      filter.limit,
      filter.offset,
    ],
  );
}

export function useDailySummary(filter: DailyRangeFilter) {
  return useReportFetch<Wrapped<DailySummaryRow>>(
    "/reports/daily-summary",
    {
      branch_id: filter.branchId || undefined,
      start_date: filter.startDate,
      end_date: filter.endDate,
    },
    [filter.branchId, filter.startDate, filter.endDate],
  );
}

export function useTopicsCoverage(filter: ReportFilter) {
  return useReportFetch<TopicsResponse>(
    "/reports/topics-coverage",
    buildQuery(filter),
    [filter.branchId, filter.startDate, filter.endDate],
  );
}

export function useBranchOverview(filter: ReportFilter) {
  return useReportFetch<Wrapped<BranchOverviewRow>>(
    "/reports/branch-overview",
    {
      start_date: filter.startDate || undefined,
      end_date: filter.endDate || undefined,
    },
    [filter.startDate, filter.endDate],
  );
}

export type ReportExportType =
  | "instructor-summary"
  | "vehicle-summary"
  | "student-summary"
  | "daily-summary"
  | "topics-coverage"
  | "branch-overview"
  | "lesson-schedule";

export type LessonScheduleScope = "daily" | "weekly" | "monthly";

export type ReportExportFormat = "csv" | "xlsx";

const EXPORT_FILENAME_BASE: Record<ReportExportType, string> = {
  "instructor-summary": "egitmen_ozeti",
  "vehicle-summary": "arac_ozeti",
  "student-summary": "kursiyer_ozeti",
  "daily-summary": "gunluk_akis",
  "topics-coverage": "konu_kapsamasi",
  "branch-overview": "sube_genel",
  "lesson-schedule": "ders_programi",
};

export async function downloadReport(
  type: ReportExportType,
  format: ReportExportFormat,
  query: QueryParams = {},
): Promise<void> {
  const params: Record<string, string | number | undefined> = { format };
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null || v === "") continue;
    params[k] = v as string | number;
  }
  const blob = await api.getBlob(`/reports/${type}/export`, params);
  const today = new Date().toISOString().slice(0, 10);
  const filename = `${EXPORT_FILENAME_BASE[type]}_${today}.${format}`;
  triggerBlobDownload(blob, filename);
}

function lessonScheduleFilename(
  scope: LessonScheduleScope,
  startDate: string,
  endDate: string,
  format: ReportExportFormat,
): string {
  if (scope === "daily") return `ders_programi_gunluk_${startDate}.${format}`;
  if (scope === "weekly") return `ders_programi_haftalik_${startDate}_${endDate}.${format}`;
  if (scope === "monthly") return `ders_programi_aylik_${startDate.slice(0, 7)}.${format}`;
  return `ders_programi_${startDate}_${endDate}.${format}`;
}

function triggerBlobDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export async function downloadLessonSchedule(
  format: ReportExportFormat,
  options: {
    scope: LessonScheduleScope;
    startDate: string;
    endDate: string;
    instructorIds?: string[];
  },
): Promise<void> {
  const params: Record<string, string> = {
    format,
    start_date: options.startDate,
    end_date: options.endDate,
    scope: options.scope,
  };
  if (options.instructorIds && options.instructorIds.length > 0) {
    params.instructor_ids = options.instructorIds.join(",");
  }
  const blob = await api.getBlob("/reports/lesson-schedule/export", params);
  triggerBlobDownload(
    blob,
    lessonScheduleFilename(options.scope, options.startDate, options.endDate, format),
  );
}

function useReportFetch<T>(
  path: string,
  query: QueryParams,
  deps: ReadonlyArray<unknown>,
): { data: T | null; loading: boolean; error: string | null; reload: () => void } {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    api
      .get<T>(path, query, { signal: controller.signal })
      .then((res) => setData(res))
      .catch((err) => {
        if (controller.signal.aborted) return;
        setError(err instanceof ApiError ? err.message : "Rapor Yüklenemedi.");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, reloadKey]);

  return { data, loading, error, reload: () => setReloadKey((k) => k + 1) };
}
